import { ArrowUpRight, ShieldCheck, HeartHandshake, Sparkles } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Stagger, Item, Reveal } from "@/components/Reveal";
import { HAIR_LOSS, IMG } from "@/lib/data";
import { SITE } from "@/lib/site";

const LOSS_FAQ = [
  {
    q: "Is the consultation private?",
    a: "Yes. Consultations are held one-to-one in a private, calm space. Everything shared is confidential.",
  },
  {
    q: "Is it non-surgical?",
    a: "Absolutely. Every solution we offer is non-surgical, non-invasive and reversible.",
  },
  {
    q: "Can I sleep, exercise and swim?",
    a: "Yes. Once fitted and cared for correctly, you can carry on with everyday life with confidence.",
  },
];

export default function HairLoss() {
  return (
    <div data-testid="page-hair-loss" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-12 lg:grid-cols-12 items-center mb-16">
          <div className="lg:col-span-6">
            <div className="overline">Hair Loss Solutions</div>
            <h1 className="font-display text-5xl sm:text-7xl mt-4 tracking-tight text-balance">
              A quiet, <span className="italic font-light">confident return.</span>
            </h1>
            <p className="mt-6 text-[hsl(var(--onyx)/0.65)] max-w-lg leading-relaxed">
              Private, non-surgical hair loss solutions. Mesh integration,
              partial mesh systems and premium toppers — thoughtfully fitted,
              beautifully finished.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="hairloss-book-consult"
                className="btn-pill btn-primary"
              >
                Book private consultation <ArrowUpRight size={16} />
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { i: ShieldCheck, t: "Private" },
                { i: HeartHandshake, t: "Judgment-free" },
                { i: Sparkles, t: "Natural finish" },
              ].map(({ i: Icon, t }) => (
                <div key={t} className="flex flex-col items-start gap-2">
                  <Icon size={18} className="text-[hsl(var(--gold))]" />
                  <span className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
                    {t}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-[2.25rem] overflow-hidden hover-zoom aspect-[4/5]">
              <img
                src={IMG.salonAlt}
                alt="Private consultation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {HAIR_LOSS.map((h) => (
            <Item key={h.slug}>
              <div
                data-testid={`hairloss-card-${h.slug}`}
                className="rounded-[2rem] overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)] h-full flex flex-col"
              >
                <div className="hover-zoom aspect-[4/5]">
                  <img
                    src={h.image}
                    alt={h.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl">{h.name}</h3>
                  <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                    {h.blurb}
                  </p>
                </div>
              </div>
            </Item>
          ))}
        </Stagger>

        <Reveal className="mt-24 max-w-3xl">
          <div className="overline">FAQs</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">
            You're safe here.
          </h2>
          <Accordion type="single" collapsible className="mt-8">
            {LOSS_FAQ.map((f, i) => (
              <AccordionItem
                key={i}
                value={`loss-faq-${i}`}
                data-testid={`loss-faq-${i}`}
                className="border-b border-[hsl(var(--onyx)/0.08)]"
              >
                <AccordionTrigger className="font-display text-xl sm:text-2xl text-left hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-[hsl(var(--onyx)/0.65)] leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  );
}
